import { Store } from "@kalyzee/kast-web-sdk";
import { AUTH_TYPE, UserOutputDTO } from "@kalyzee/kast-web-sdk/http";
import { useState } from "react";
import { CompatibilityInfos } from "../../helpers/sdk";
import { Settings } from "../../helpers/settings";
import { getMediaParam, MediaKeys, setMediaParam } from "../../helpers/storage";
import { StudentMeetingOptions } from "../../interfaces/meeting";
import { SecondaryScreen } from "../../interfaces/secondaryScreens";
import { SessionMode } from "./hooks";
import { LayoutType, MOBILE_BREAKPOINT } from "../../interfaces/ui";

export interface AppState {
  user?: UserOutputDTO;
  userFetchStatus: "connecting" | "connected" | "disconnected";
  mode?: SessionMode;
  screen?: SecondaryScreen;
  disableGoogleLogin?: boolean;
  microphone?: boolean;
  compatibilityInfos?: Partial<CompatibilityInfos>;
  authMethods?: AUTH_TYPE[];
  settings?: Settings;
  lastLeftMeetingId?: string;

  layout: LayoutType;

  mainClock: Date;

  audioStreamEnabled: boolean;
  videoStreamEnabled: boolean;

  selfStreams: { [sourceName: string]: MediaStream };
  sourcePreferences: { [sourceName: string]: { audioEnabled?: boolean; videoEnabled?: boolean } };

  meetingOptions: {
    [meetingId: string]: StudentMeetingOptions;
  };

  inputAudioDeviceInfo?: MediaDeviceInfo;
  inputVideoDeviceInfo?: MediaDeviceInfo;
  outputAudioDeviceInfo?: MediaDeviceInfo;

  workBoxMode: "inputs" | "survey" | "highlight" | "chat";
  workBoxOpen: boolean;
  /**
   * Currently visible popup overlay (always-mounted overlays in the meeting page).
   * `undefined` means no popup is shown.
   */
  popupMode?: "sources" | "share";

  isMobile: boolean;
}

export interface AppStoreReducers {
  setUser: UserOutputDTO | undefined;
  setUserFetchStatus: "connecting" | "connected" | "disconnected";
  setMode: SessionMode | undefined;
  setScreen: SecondaryScreen;
  setDisableGoogleLogin: boolean;
  setMicrophone: boolean;
  setCompatibilityInfos: Partial<CompatibilityInfos> | undefined;
  setAuthMethods: AUTH_TYPE[] | undefined;
  setLastLeftMeetingId: string;

  setLayout: LayoutType;

  setMainClock: Date;

  setAudioStreamEnabled: boolean;
  setVideoStreamEnabled: boolean;

  updateSelfStream: { sourceName: string, stream?: MediaStream };
  updateSourcePreference: { sourceName: string; audioEnabled?: boolean; videoEnabled?: boolean };
  setSourcePreferences: { [sourceName: string]: { audioEnabled?: boolean; videoEnabled?: boolean } };

  updateMeetingOptions: { meetingId: string, options: StudentMeetingOptions };

  setInputAudioDeviceInfo: MediaDeviceInfo | undefined;
  setInputVideoDeviceInfo: MediaDeviceInfo | undefined;
  setOutputAudioDeviceInfo: MediaDeviceInfo | undefined;

  setWorkBoxMode: "inputs" | "survey" | "highlight" | "chat";
  setWorkBoxOpen: boolean;
  setPopupMode: "sources" | "share" | undefined;

  setIsMobile: boolean;
}

type AppStoreReducerAction<T extends keyof AppStoreReducers = keyof AppStoreReducers> = {
  type: T;
  payload: AppStoreReducers[T];
};


const actionIsType = <T extends keyof AppStoreReducers>(
  action: AppStoreReducerAction,
  type: T,
): action is AppStoreReducerAction<T> => {
  return action.type === type;
};

export class AppStore extends Store<AppState> {

  private reducer (
    action: AppStoreReducerAction,
  ) {
    if (actionIsType(action, "setUser")) {
      this.updateState(draft => {
        draft.user = action.payload;
      });
    } else if (actionIsType(action, "setUserFetchStatus")) {
      this.updateState(draft => {
        draft.userFetchStatus = action.payload;
      });
    } else if (actionIsType(action, "setMode")) {
      this.updateState(draft => {
        draft.mode = action.payload;
      });
    } else if (actionIsType(action, "setScreen")) {
      this.updateState(draft => {
        draft.screen = action.payload;
      });
    } else if (actionIsType(action, "setDisableGoogleLogin")) {
      this.updateState(draft => {
        draft.disableGoogleLogin = action.payload;
      });
    } else if (actionIsType(action, "setMicrophone")) {
      this.updateState(draft => {
        draft.microphone = action.payload;
      });
    } else if (actionIsType(action, "setCompatibilityInfos")) {
      this.updateState(draft => {
        draft.compatibilityInfos = action.payload;
      });
    } else if (actionIsType(action, "setAuthMethods")) {
      this.updateState(draft => {
        draft.authMethods = action.payload;
      });
    } else if (actionIsType(action, "setLastLeftMeetingId")) {
      this.updateState(draft => {
        draft.lastLeftMeetingId = action.payload;
      })
    } else if (actionIsType(action, "setLayout")) {
      this.updateState(draft => {
        draft.layout = action.payload;
      });
    } else if (actionIsType(action, "setMainClock")) {
      this.updateState(draft => {
        draft.mainClock = action.payload;
      })
    } else if (actionIsType(action, "setAudioStreamEnabled")) {
      setMediaParam(MediaKeys.INTPUT_AUDIO_ENABLED, action.payload);
      this.updateState(draft => {
        draft.audioStreamEnabled = action.payload;
      });
    } else if (actionIsType(action, "setVideoStreamEnabled")) {
      setMediaParam(MediaKeys.INTPUT_VIDEO_ENABLED, action.payload);
      this.updateState(draft => {
        draft.videoStreamEnabled = action.payload;
      });
    } else if (actionIsType(action, "updateSelfStream")) {
      this.updateState(draft => {
        const { sourceName, stream } = action.payload;
        if (stream) {
          draft.selfStreams[sourceName] = stream;
        } else {
          delete draft.selfStreams[sourceName];
        }
      });
    } else if (actionIsType(action, "updateSourcePreference")) {
      this.updateState(draft => {
        const { sourceName, audioEnabled, videoEnabled } = action.payload;
        if (!draft.sourcePreferences[sourceName]) draft.sourcePreferences[sourceName] = {};
        if (audioEnabled !== undefined) draft.sourcePreferences[sourceName].audioEnabled = audioEnabled;
        if (videoEnabled !== undefined) draft.sourcePreferences[sourceName].videoEnabled = videoEnabled;
      });
      setMediaParam(MediaKeys.SOURCE_PREFERENCES, this.getState().sourcePreferences);
    } else if (actionIsType(action, "setSourcePreferences")) {
      this.updateState(draft => {
        draft.sourcePreferences = action.payload;
      });
      setMediaParam(MediaKeys.SOURCE_PREFERENCES, action.payload);
    } else if (actionIsType(action, "updateMeetingOptions")) {
      this.updateState(draft => {
        const { meetingId, options } = action.payload;
        draft.meetingOptions = draft.meetingOptions || {};
        draft.meetingOptions[meetingId] = {
          ...draft.meetingOptions[meetingId],
          ...options,
        };
      });
    } else if (actionIsType(action, "setInputAudioDeviceInfo")) {
      this.updateState(draft => {
        draft.inputAudioDeviceInfo = action.payload;
      });
    } else if (actionIsType(action, "setInputVideoDeviceInfo")) {
      this.updateState(draft => {
        draft.inputVideoDeviceInfo = action.payload;
      });
    } else if (actionIsType(action, "setOutputAudioDeviceInfo")) {
      this.updateState(draft => {
        draft.outputAudioDeviceInfo = action.payload;
      });
    } else if (actionIsType(action, "setWorkBoxMode")) {
      this.updateState(draft => {
        draft.workBoxMode = action.payload;
      });
    } else if (actionIsType(action, "setWorkBoxOpen")) {
      this.updateState(draft => {
        draft.workBoxOpen = action.payload;
      });
    } else if (actionIsType(action, "setPopupMode")) {
      this.updateState(draft => {
        draft.popupMode = action.payload;
      });
    } else if (actionIsType(action, "setIsMobile")) {
      this.updateState(draft => {
        draft.isMobile = action.payload;
      });
    }
  }

  dispatch<T extends keyof AppStoreReducers>(type: T, payload: AppStoreReducers[T]): void {
    this.reducer({
      type,
      payload,
    });
  }

}

export const useAppStore = (): AppStore => {
  const [{ current }] = useState(() => {
    return ({
      current: new AppStore({
        disableGoogleLogin: false,
        userFetchStatus: "connecting",

        mainClock: new Date(),

        audioStreamEnabled: getMediaParam(MediaKeys.INTPUT_AUDIO_ENABLED) ?? false,
        videoStreamEnabled: getMediaParam(MediaKeys.INTPUT_VIDEO_ENABLED) ?? false,

        selfStreams: {},
        sourcePreferences: getMediaParam(MediaKeys.SOURCE_PREFERENCES) ?? {},

        meetingOptions: {},

        layout: LayoutType.Bottom,

        workBoxMode: "inputs",
        workBoxOpen: false,

        isMobile: typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false,
      })
    })
  });

  return current;
};